/**
 * launchDensity — the compact launch form's field classes (story 28633: "no
 * scrolling in a ~440×720 pane").
 *
 * ONE place for the three things every launch field repeats — its label, its
 * input box and its helper line — so the Policy, action, address and run-option
 * controls cannot drift apart again. Text uses the pane's three styles
 * (`paneTypography`): labels are TYPE_LABEL, helper lines TYPE_BODY, and input
 * text is 13px (TYPE_TITLE's size, regular weight).
 *
 * Input text goes back to 16px on a COARSE pointer only: below 16px a phone's
 * browser zooms the page on focus, which is why these inputs used to be
 * `text-base sm:text-sm`. Keyed on the pointer, like `touch-target`, not on
 * viewport width — the Outlook pane is always below `sm:`, so a width breakpoint
 * kept the desktop pane at 16px too.
 *
 * Shared — no Office.js, no Remix import.
 */
import { TYPE_BODY, TYPE_LABEL } from "~/common/workflows/paneTypography";

/** Input/select/trigger text: 13px, 16px on a finger. */
export const COMPACT_INPUT_TEXT =
  "text-[13px] leading-[19px] [@media(pointer:coarse)]:text-base";

/** The box every text input, select and select-like trigger shares. */
export const COMPACT_INPUT_BOX =
  "rounded-md border border-primary-gray-light-300 bg-base-white px-2.5 py-1.5 text-primary-gray-light-900 disabled:bg-primary-gray-light-50 disabled:text-primary-gray-light-700";

/** Full input class: box + text. */
export const COMPACT_INPUT = `${COMPACT_INPUT_BOX} ${COMPACT_INPUT_TEXT}`;

/**
 * THE height of every single-line control in the pane — text input, select,
 * date picker, select-like trigger, the policy combobox and the client search
 * (story 28633: "the same consistent height as all input controls in the
 * plugin"). 34px is a text input's natural height at COMPACT_INPUT's padding;
 * the native date picker and MUI's comboboxes used to come out taller. A
 * coarse pointer still gets `touch-target`'s 44px floor (min-height wins).
 * Multi-line inputs (textarea) never take it.
 */
export const COMPACT_CONTROL_HEIGHT_PX = 34;
export const COMPACT_CONTROL_HEIGHT = "h-[34px]";

/** A single-line control: COMPACT_INPUT at the one control height. */
export const COMPACT_SINGLE_LINE = `${COMPACT_INPUT} ${COMPACT_CONTROL_HEIGHT}`;

/** A field's label (and a radio group's legend). */
export const COMPACT_FIELD_LABEL = `flex items-center gap-1.5 ${TYPE_LABEL} text-primary-gray-light-600`;

/**
 * Inline extras inside a label (confidence chip, required star) must not
 * inherit the label's uppercase / tracking.
 */
export const LABEL_INLINE_RESET = "normal-case tracking-normal";

/** A helper / note line under a field. */
export const COMPACT_HELPER = `${TYPE_BODY} text-primary-gray-light-500`;
